import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _sliced_to_array } from "@swc/helpers/_/_sliced_to_array";
import { _ as _to_array } from "@swc/helpers/_/_to_array";
import { _ as _to_consumable_array } from "@swc/helpers/_/_to_consumable_array";
import { PLACEHOLDER_IMAGE_URL } from "@faire/retailer-visitor-shared/consts/PLACEHOLDER_IMAGE_URL";
import { filterInvalidImages } from "@faire/retailer-visitor-shared/lib/filterInvalidImages";
import { canShowVideoProducts } from "@faire/retailer-visitor-shared/views/product/ProductImages/showProductVideos";
import { isFastly } from "@faire/web--source/common/images/optimizeImages";
import { isNotUndefined } from "@faire/web--source/common/typescriptUtils";
import { IImage } from "@faire/web-api--source/indigofair/data/IImage";
import isEmpty from "lodash/isEmpty";
import { useState, useMemo, useCallback } from "react";
var PLACEHOLDER_IMAGE = IImage.build({
    token: "p_placeholder",
    url: PLACEHOLDER_IMAGE_URL
});
export var useProductImageOrVideoViewState = function(initialImages, initialVideos) {
    var _useState = _sliced_to_array(useState(function() {
        var internal = initialImages === null || initialImages === void 0 ? void 0 : initialImages.filter(function(i) {
            return i.url && isFastly(i.url);
        });
        return (internal === null || internal === void 0 ? void 0 : internal.length) ? internal : undefined;
    }), 2), validImages = _useState[0], setValidImages = _useState[1];
    var videos = useMemo(function() {
        return initialVideos && canShowVideoProducts() ? initialVideos.filter(function(v) {
            var _v_video;
            return isNotUndefined((_v_video = v.video) === null || _v_video === void 0 ? void 0 : _v_video.url);
        }) : [];
    }, [
        initialVideos
    ]);
    var _useState1 = _sliced_to_array(useState(0), 2), selectedIndex = _useState1[0], setSelectedIndex = _useState1[1];
    var images = useMemo(function() {
        var imagesOrPlaceholder = function(imgs) {
            return !imgs || isEmpty(imgs) ? [
                PLACEHOLDER_IMAGE
            ] : imgs;
        };
        return imagesOrPlaceholder(validImages).filter(function(i) {
            return isNotUndefined(i.url);
        });
    }, [
        validImages
    ]);
    var itemsCount = images.length + videos.length;
    var isSelectionAnImage = useMemo(function() {
        return selectedIndex < images.length;
    }, [
        selectedIndex,
        images
    ]);
    var getProductMedia = useCallback(function() {
        var imageMedia = images.map(function(image) {
            return {
                isVideo: false,
                image: image
            };
        });
        var videoMedia = videos.map(function(productVideo) {
            var video = productVideo.video;
            if (video === null || video === void 0 ? void 0 : video.thumbnail_image) {
                return {
                    isVideo: true,
                    video: video
                };
            } else {
                return undefined;
            }
        }).filter(isNotUndefined);
        var _imageMedia = _to_array(imageMedia), firstImage = _imageMedia[0], restImages = _imageMedia.slice(1);
        var _videoMedia = _to_array(videoMedia), firstVideo = _videoMedia[0], restVideos = _videoMedia.slice(1);
        return [
            firstImage,
            firstVideo
        ].concat(_to_consumable_array(restImages), _to_consumable_array(restVideos)).filter(isNotUndefined).map(function(media, index) {
            return _object_spread_props(_object_spread({}, media), {
                index: index
            });
        });
    }, [
        images,
        videos
    ]);
    var selectedImage = useMemo(function() {
        return isSelectionAnImage ? images[selectedIndex] : undefined;
    }, [
        isSelectionAnImage,
        images,
        selectedIndex
    ]);
    var selectedVideo = useMemo(function() {
        return !isSelectionAnImage ? videos[selectedIndex - images.length] : undefined;
    }, [
        isSelectionAnImage,
        videos,
        selectedIndex,
        images.length
    ]);
    var setImages = useCallback(function(newImages) {
        filterInvalidImages(newImages).then(setValidImages);
    }, []);
    var setIndex = useCallback(function(index) {
        if (index < 0) {
            setSelectedIndex(itemsCount - 1);
        } else if (index >= itemsCount) {
            setSelectedIndex(0);
        } else {
            setSelectedIndex(index);
        }
    }, [
        itemsCount
    ]);
    return {
        selectedIndex: selectedIndex,
        isSelectionAnImage: isSelectionAnImage,
        images: images,
        videos: videos,
        getProductMedia: getProductMedia,
        selectedImage: selectedImage,
        selectedVideo: selectedVideo,
        setImages: setImages,
        setIndex: setIndex
    };
};
